// icons
import {
  faCheckSquare,
  faChevronDown,
  faChevronRight,
  faMinusSquare,
  faPlusSquare,
  faSquare,
  faStream,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Grid } from '@material-ui/core';
import React from 'react';
import CheckboxTree from 'react-checkbox-tree';

import { IPermission, IPermissionsTemplate } from '~/src/dash/api/permissions/model';
import { ModelPrimaryButton } from '~/src/dash/components/model-primary-button';
import { ModelSecondaryButton } from '~/src/dash/components/model-secondary-button';
import { isSamsungPublisher } from '~/src/dash/utils/ui';
import { H3 } from '~/src/dash/views/component-view';

interface INode {
  label: React.ReactNode;
  value: string;
  children?: INode[];
  className?: string;
  disabled?: boolean;
  icon?: React.ReactNode;
  showCheckbox?: boolean;
  title?: string;
}

interface IState {
  nodes: INode[];
  filterText: string;
  nodesFiltered: INode[];
  checked: string[];
  expanded: string[];
  disabled: boolean;
}

interface IProps {
  permissionsTemplate: IPermissionsTemplate;
  selectedPermissions: string;
  onCheckedChanges: (checked: string[]) => void;
  disabled?: boolean;
  savePermissions: () => void;
  publisherID: string;
}

export class PermissionTree extends React.Component<IProps, IState> {
  state = {
    checked: [] as string[],
    expanded: [] as string[],
    nodes: [] as INode[],
    filterText: '',
    nodesFiltered: [] as INode[],
    disabled: false,
  };

  allNodes = [] as string[];

  constructor(props: IProps) {
    super(props);

    const nodes = this.transformPermissionTemplateToTreeNode(props.permissionsTemplate) as INode[];
    const nodesFiltered = nodes.reduce(this.filterNodes, []);

    this.state = {
      nodes,
      nodesFiltered,
      expanded: this.allNodes,
      filterText: '',
      checked: this.propagatePermissionsToChildrens(props.selectedPermissions.split(','), nodes),
      disabled: props.disabled || false,
    };
  }

  extractNodeNames = (src: INode): string[] => {
    const out = [];
    out.push(src.value);
    for (const ch of src.children || []) {
      out.push(...this.extractNodeNames(ch));
    }
    return out;
  };

  findNode = (src: INode, value: string): INode | undefined => {
    if (src.value === value) {
      return src;
    }
    for (const ch of src.children || []) {
      const test = this.findNode(ch, value);
      if (test !== undefined) {
        return test;
      }
    }
    return undefined;
  };

  propagatePermissionsToChildrens = (permissions: string[], src: INode[]): string[] => {
    const newPerms = [] as string[];

    for (let perm of permissions) {
      perm = perm.replace(' ', '');
      if (!newPerms.includes(perm)) {
        newPerms.push(perm);

        // look for all childrens
        for (const n of src) {
          const rootNode = this.findNode(n, perm);
          if (rootNode !== undefined) {
            // get all name
            const names = this.extractNodeNames(rootNode);
            for (const name of names) {
              if (!newPerms.includes(name)) {
                newPerms.push(name);
              }
            }
          }
        }
      }
    }

    return newPerms;
  };

  componentDidUpdate(prevProps: Readonly<IProps>) {
    if (prevProps.selectedPermissions !== this.props.selectedPermissions) {
      const checked = this.propagatePermissionsToChildrens(
        this.props.selectedPermissions.split(','),
        this.state.nodes,
      );

      this.setState({
        checked,
      });
    }

    if (prevProps.disabled !== this.props.disabled) {
      this.setState({
        disabled: this.props.disabled || false,
      });
    }
  }

  checkAllReadNodes = () => {
    const readOnlyNodes = [] as string[];

    for (const name of this.allNodes) {
      if (name.startsWith('read_')) {
        readOnlyNodes.push(name);
      }
    }

    this.setState({
      checked: readOnlyNodes,
    });

    this.props.onCheckedChanges(readOnlyNodes);
  };

  customLabel = (perm: IPermission): React.ReactNode => {
    let displayName = perm.description || '';
    if (displayName === '') {
      displayName = perm.name;
    }

    return <span style={{ fontSize: '18px' }}>{displayName}</span>;
  };

  recursiveProcessNode = (currentNodePermission: IPermission): INode[] | null => {
    const node: INode[] = [];
    for (const perm of currentNodePermission.permissions || []) {
      this.allNodes.push(perm.name);

      node.push({
        value: perm.name,
        label: this.customLabel(perm),
        title: perm.description,
        children: this.recursiveProcessNode(perm),
      } as INode);
    }

    if (node.length === 0) {
      return null;
    }

    return node;
  };

  transformPermissionTemplateToTreeNode = (
    permissionTemplate: IPermissionsTemplate,
  ): INode[] | null => {
    const nodes = [] as INode[];

    this.allNodes.push(permissionTemplate.root.name);

    nodes.push({
      value: permissionTemplate.root.name,
      label: this.customLabel(permissionTemplate.root),
      title: permissionTemplate.root.description,
      children: [],
    } as INode);

    for (const perm of permissionTemplate.root.permissions || []) {
      this.allNodes.push(perm.name);

      // @ts-ignore
      nodes[0].children.push({
        value: perm.name,
        label: this.customLabel(perm),
        title: perm.description,
        children: this.recursiveProcessNode(perm),
      } as INode);
    }

    if (nodes.length === 0) {
      return null;
    }

    return nodes;
  };

  onFilterChange = (e: any) => {
    this.setState({ filterText: e.target.value }, this.filterTree);
  };

  filterTree = () => {
    // Reset nodes back to unfiltered state
    if (!this.state.filterText) {
      this.setState({
        nodesFiltered: this.state.nodes,
      });

      return;
    }

    const nodesFiltered = this.state.nodesFiltered.reduce(this.filterNodes, []);

    this.setState({
      nodesFiltered,
    });
  };

  filterNodes = (filtered: INode[], node: INode) => {
    const { filterText } = this.state;
    const { publisherID } = this.props;
    let children = (node.children || []).reduce(this.filterNodes, []);

    // this handles the filtering of nodes via the search textbox (disabled for now bc we don't have a lot of nodes)
    if (
      // Node's label matches the search string
      (node.title || '').toLocaleLowerCase().indexOf(filterText.toLocaleLowerCase()) > -1 ||
      // Or a children has a matching node
      children.length
    ) {
      if (children.length === 0) {
        // @ts-ignore
        children = undefined;
      }
      filtered.push({
        ...node,
        children,
      });
    }

    // samsung have a special role "write_bidder_subtier", only available for their publishers
    // we need to filter this role for all non-samsung publishers
    if (!isSamsungPublisher(publisherID)) {
      filtered = filtered.filter((node) => node.value !== 'write_bidder_subtier');
    }

    return filtered;
  };

  render() {
    if (this.state.nodes.length > 0) {
      return (
        <div className='filter-container' style={{ padding: '10px' }}>
          <Grid container direction='row' justify='flex-start' alignItems='center' spacing={3}>
            {/* Search Box, disabled for now because we don't have a lot of permissions */}
            {/* <Grid item>*/}
            {/*    <TextField*/}
            {/*        placeholder='Search...'*/}
            {/*        variant='outlined'*/}
            {/*        value={this.state.filterText}*/}
            {/*        onChange={this.onFilterChange}*/}
            {/*    />*/}
            {/* </Grid>*/}
            <Grid item>
              <H3>Permissions</H3>
            </Grid>
            <Grid item style={{ marginLeft: '100px' }}>
              <ModelPrimaryButton
                onClick={this.props.savePermissions}
                disabled={this.props.disabled}
              >
                Save
              </ModelPrimaryButton>
            </Grid>
          </Grid>
          <Grid item style={{ marginBottom: '14px' }}>
            <ModelSecondaryButton onClick={this.checkAllReadNodes}>
              Check All Read-Only
            </ModelSecondaryButton>
          </Grid>
          <CheckboxTree
            nodes={this.state.nodesFiltered}
            checked={this.state.checked}
            expanded={this.state.expanded}
            onCheck={(checked) => {
              this.setState({ checked });
              this.props.onCheckedChanges(checked);
            }}
            onExpand={(expanded) => this.setState({ expanded })}
            // noCascade
            nativeCheckboxes
            checkModel={'all'}
            icons={{
              check: <FontAwesomeIcon className='rct-icon rct-icon-check' icon={faCheckSquare} />,
              uncheck: <FontAwesomeIcon className='rct-icon rct-icon-uncheck' icon={faSquare} />,
              halfCheck: (
                <FontAwesomeIcon className='rct-icon rct-icon-half-check' icon={faCheckSquare} />
              ),
              expandClose: (
                <FontAwesomeIcon className='rct-icon rct-icon-expand-close' icon={faChevronRight} />
              ),
              expandOpen: (
                <FontAwesomeIcon className='rct-icon rct-icon-expand-open' icon={faChevronDown} />
              ),
              expandAll: (
                <FontAwesomeIcon className='rct-icon rct-icon-expand-all' icon={faPlusSquare} />
              ),
              collapseAll: (
                <FontAwesomeIcon className='rct-icon rct-icon-collapse-all' icon={faMinusSquare} />
              ),
              parentClose: (
                <FontAwesomeIcon className='rct-icon rct-icon-parent-close' icon={faStream} />
              ),
              parentOpen: (
                <FontAwesomeIcon className='rct-icon rct-icon-parent-open' icon={faStream} />
              ),
              leaf: undefined,
            }}
            disabled={this.state.disabled}
          />
        </div>
      );
    }
    return <div>loading</div>;
  }
}
